import React, { createContext, useContext, useState, useEffect } from 'react';
import modernTheme from './theme-modern';
import themeTextsJson from '../data/theme-texts.json';
import themeIconsJson from '../data/theme-icons.json';

const themes = {
  modern: modernTheme,
};

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    return {
      theme: themes.modern,
      themeName: 'modern',
      themeTexts: themeTextsJson.modern,
      themeIcons: themeIconsJson.modern,
      isDark: false,
      toggleDarkMode: () => {
        const root = document.documentElement;
        const isDark = root.classList.contains('dark');
        if (isDark) {
          root.classList.remove('dark');
        } else {
          root.classList.add('dark');
        }
        localStorage.setItem('darkMode', (!isDark).toString());
      },
      forceLightMode: () => {
        const root = document.documentElement;
        root.classList.remove('dark');
        localStorage.setItem('darkMode', 'false');
      },
      restoreTheme: () => {
        const stored = localStorage.getItem('darkMode');
        const root = document.documentElement;
        if (stored === 'true') {
          root.classList.add('dark');
        } else {
          root.classList.remove('dark');
        }
      }
    };
  }
  return context;
};

export const useOnboardingTheme = () => {
  const { forceLightMode, restoreTheme } = useTheme();
  
  useEffect(() => {
    // Force light mode for onboarding
    forceLightMode();
    
    // Restore theme when component unmounts
    return () => {
      restoreTheme();
    };
  }, [forceLightMode, restoreTheme]);
};

export const ThemeProvider = ({ children }) => {
  const [themeName, setThemeName] = useState(() => localStorage.getItem('theme') || 'modern');
  const [isDark, setIsDark] = useState(() => {
    // Check localStorage first, then DOM class, then system preference
    const stored = localStorage.getItem('darkMode');
    if (stored !== null) {
      return stored === 'true';
    }
    return document.documentElement.classList.contains('dark') || 
           window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Initialize theme on mount
  useEffect(() => {
    const root = document.documentElement;
    const stored = localStorage.getItem('darkMode');
    
    if (stored !== null) {
      const shouldBeDark = stored === 'true';
      if (shouldBeDark) {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
      setIsDark(shouldBeDark);
    } else {
      // Use system preference as default
      const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (systemPrefersDark) {
        root.classList.add('dark');
        setIsDark(true);
        localStorage.setItem('darkMode', 'true');
      } else {
        root.classList.remove('dark');
        setIsDark(false);
        localStorage.setItem('darkMode', 'false');
      }
    }
  }, []);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const domIsDark = document.documentElement.classList.contains('dark');
      if (domIsDark !== isDark) {
        console.warn('Theme mismatch detected:', { contextIsDark: isDark, domIsDark });
        setIsDark(domIsDark);
      }
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, [isDark]);

  const toggleDarkMode = () => {
    const root = document.documentElement;
    const newDarkMode = !isDark;
    
    if (newDarkMode) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    
    // Update state
    setIsDark(newDarkMode);
    
    // Update localStorage
    localStorage.setItem('darkMode', newDarkMode.toString());
  };

  const forceLightMode = () => {
    const root = document.documentElement;
    root.classList.remove('dark');
    setIsDark(false);
    localStorage.setItem('darkMode', 'false');
  };

  const restoreTheme = () => {
    const stored = localStorage.getItem('darkMode');
    const root = document.documentElement;
    if (stored === 'true') {
      root.classList.add('dark');
      setIsDark(true);
    } else {
      root.classList.remove('dark');
      setIsDark(false);
    }
  };

  const theme = modernTheme[isDark ? 'dark' : 'light'];
  const themeTexts = themeTextsJson[themeName] || themeTextsJson.modern;
  const themeIcons = themeIconsJson[themeName] || themeIconsJson.modern;

  useEffect(() => {
    localStorage.setItem('theme', themeName);
  }, [themeName]);

  // Synchronize theme state with DOM
  useEffect(() => {
    const root = document.documentElement;
    if (isDark && !root.classList.contains('dark')) {
      root.classList.add('dark');
    } else if (!isDark && root.classList.contains('dark')) {
      root.classList.remove('dark');
    }
  }, [isDark]);

  useEffect(() => {
    // Inject Google Font link
    let link = document.getElementById('dynamic-theme-font');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'stylesheet';
      link.id = 'dynamic-theme-font';
      document.head.appendChild(link);
    }
    link.href = theme.fontUrl;
    // Set global font-family
    document.body.style.fontFamily = theme.fontFamily;
    return () => {
      // Optionally clean up font link if needed
    };
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ 
      theme, 
      themeName, 
      setThemeName, 
      themeTexts, 
      themeIcons, 
      isDark, 
      toggleDarkMode,
      forceLightMode,
      restoreTheme
    }}>
      {children}
    </ThemeContext.Provider>
  );
};